import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import './index.scss';
import './styles.css';
import './styles/theme.css';

// V2 Components - The default UI
import { AppShell } from './v2/layouts/AppShell';
// V2 Auth Pages - loaded eagerly (needed for initial render)
import {
  LoginPage as V2LoginPage,
  ForgotPasswordPage as V2ForgotPasswordPage,
  ResetPasswordPage as V2ResetPasswordPage,
  SignupPage,
  VerifyMagicLinkPage,
  VerifyEmailPage
} from './v2/pages/auth';
import { AuthProvider, ProtectedRoute } from './v2/providers/AuthProvider';
import { ErrorBoundary } from './v2/components/ErrorBoundary';
import { loadBuildInfo } from './utils/buildInfo';
// Lazy-loaded pages - code-split into separate chunks for faster initial load
const AssistantPage = React.lazy(() =>
  import('./v2/pages/assistant').then((m) => ({ default: m.AssistantPage }))
);
const UnifiedChannelsPage = React.lazy(() =>
  import('./v2/pages/channels/UnifiedChannelsPage').then((m) => ({
    default: m.UnifiedChannelsPage
  }))
);
const DashboardPage = React.lazy(() =>
  import('./v2/pages/dashboard').then((m) => ({ default: m.DashboardPage }))
);
const SettingsPage = React.lazy(() =>
  import('./v2/pages/settings').then((m) => ({ default: m.SettingsPage }))
);
const AgentsPage = React.lazy(() =>
  import('./v2/pages/agents').then((m) => ({ default: m.AgentsPage }))
);
const ClipPage = React.lazy(() => import('./v2/pages/clip'));
const SubsPage = React.lazy(() => import('./v2/pages/subs').then((m) => ({ default: m.SubsPage })));

// ============================================================
// V1 → V2 Redirect Helpers
// ============================================================

/**
 * Redirect V1 channel list to V2
 */
const V1ChannelsRedirect: React.FC = () => {
  const params = window.location.pathname.match(/\/organizations\/([^/]+)\/channels/);
  const orgId = params?.[1];
  return <Navigate to={orgId ? `/v2/organizations/${orgId}/channels` : '/v2/channels'} replace />;
};

/**
 * Redirect V1 channel detail to V2
 */
const V1ChannelDetailRedirect: React.FC = () => {
  const params = window.location.pathname.match(/\/organizations\/([^/]+)\/channels\/([^/]+)/);
  const orgId = params?.[1];
  const channelId = params?.[2];
  return (
    <Navigate
      to={orgId && channelId ? `/v2/organizations/${orgId}/channels/${channelId}` : '/v2/channels'}
      replace
    />
  );
};

/**
 * Redirect V1 settings-related pages to V2 settings with tab
 */
const V1SettingsRedirect: React.FC<{ tab: string }> = ({ tab }) => {
  return <Navigate to={`/v2/settings?tab=${tab}`} replace />;
};

const renderLoader = () => (
  <div className="loader-container">
    <div className="spinner"></div>
  </div>
);

const App: React.FC = () => {
  useEffect(() => {
    loadBuildInfo();
  }, []);

  return (
    <BrowserRouter>
      <Suspense fallback={renderLoader()}>
        <Routes>
          {/* ============================================================
                PUBLIC ROUTES - No auth required
                ============================================================ */}
          {/* Login - redirects to /v2/channels after auth */}
          <Route
            path="/"
            element={
              <ErrorBoundary>
                <V2LoginPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="/login"
            element={
              <ErrorBoundary>
                <V2LoginPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="/v2/login"
            element={
              <ErrorBoundary>
                <V2LoginPage />
              </ErrorBoundary>
            }
          />

          {/* Password reset */}
          <Route
            path="/forgot-password"
            element={
              <ErrorBoundary>
                <V2ForgotPasswordPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="/v2/forgot-password"
            element={
              <ErrorBoundary>
                <V2ForgotPasswordPage />
              </ErrorBoundary>
            }
          />

          {/* Password reset (token callback from email) */}
          <Route
            path="/v2/reset-password"
            element={
              <ErrorBoundary>
                <V2ResetPasswordPage />
              </ErrorBoundary>
            }
          />

          {/* Magic link verification (token callback from email) */}
          <Route
            path="/v2/verify-magic-link"
            element={
              <ErrorBoundary>
                <VerifyMagicLinkPage />
              </ErrorBoundary>
            }
          />

          {/* Email verification (token callback from email) */}
          <Route
            path="/v2/verify-email"
            element={
              <ErrorBoundary>
                <VerifyEmailPage />
              </ErrorBoundary>
            }
          />

          {/* Signup */}
          <Route
            path="/signup"
            element={
              <ErrorBoundary>
                <SignupPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="/v2/signup"
            element={
              <ErrorBoundary>
                <SignupPage />
              </ErrorBoundary>
            }
          />

          {/* Public clip viewer */}
          <Route path="/clip/" element={<ClipPage />} />

          {/* ============================================================
                V2 ROUTES - Default UI with BetterAuth
                ============================================================ */}
          <Route
            path="/v2"
            element={
              <AuthProvider>
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              </AuthProvider>
            }
          >
            {/* Index route - redirect to channels */}
            <Route index element={<Navigate to="channels" replace />} />

            {/* Main pages. Alerts is served under Agents as the `alerts`
                registry entry (/v2/agents/alerts, ?view=manage deep-links to
                the management pane) — see v2/agents/registry.ts. */}
            <Route path="assistant" element={<AssistantPage />} />
            <Route path="agents" element={<AgentsPage />} />
            <Route path="agents/:slug" element={<AgentsPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="organizations/:org_id/dashboard" element={<DashboardPage />} />
            <Route path="subs" element={<SubsPage />} />
            {/* Legacy manage-orgs redirect → Settings Channels tab */}
            <Route
              path="manage-orgs"
              element={<Navigate to="/v2/settings?tab=channels" replace />}
            />
            <Route path="settings" element={<SettingsPage />} />

            {/* Unified channels view */}
            <Route path="channels" element={<UnifiedChannelsPage />} />
            <Route path="organizations/:org_id/channels" element={<UnifiedChannelsPage />} />
            <Route
              path="organizations/:org_id/channels/:channel_id"
              element={<UnifiedChannelsPage />}
            />

            {/* Feed view (alias for channels) */}
            <Route path="feed" element={<UnifiedChannelsPage />} />
            <Route path="organizations/:org_id/feed" element={<UnifiedChannelsPage />} />
          </Route>

          {/* ============================================================
                LEGACY V1 REDIRECTS - Preserve old bookmarks/links
                ============================================================ */}
          {/* Dashboard */}
          <Route path="/dashboard" element={<Navigate to="/v2/dashboard" replace />} />

          {/* Channels */}
          <Route path="/organizations/:org_id/channels" element={<V1ChannelsRedirect />} />
          <Route
            path="/organizations/:org_id/channels/:channel_id"
            element={<V1ChannelDetailRedirect />}
          />

          {/* Settings-related pages → V2 settings */}
          <Route path="/organizations/:org_id/users" element={<V1SettingsRedirect tab="users" />} />
          <Route
            path="/organizations/:org_id/invitations"
            element={<V1SettingsRedirect tab="users" />}
          />
          <Route
            path="/organizations/:org_id/speakers"
            element={<V1SettingsRedirect tab="speakers" />}
          />
          <Route path="/organizations/:org_id/tags" element={<V1SettingsRedirect tab="tags" />} />
          <Route
            path="/organizations/:org_id/alerts"
            element={<Navigate to="/v2/agents/alerts" replace />}
          />
          <Route path="/my-phone-numbers" element={<Navigate to="/v2/settings" replace />} />

          {/* Assistant */}
          <Route path="/assistant" element={<Navigate to="/v2/assistant" replace />} />

          {/* Catch-all: redirect to V2 channels */}
          <Route path="*" element={<Navigate to="/v2/channels" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default App;
